/**
 * Duraciones ofrecidas para el inicio elegido, con el precio que informa el servidor.
 *
 * ## Por que una duracion puede no aparecer
 *
 * La tabla de tarifas es la que decide: **sin precio para el par (inicio, duracion), no
 * hay reserva** (ADR-010). El caso real es `16:30` con 90 minutos, que no existe en la
 * tabla. El servidor simplemente no manda esa opcion, y aca se explica por que, en vez
 * de dejar a la persona buscando un boton que no esta.
 *
 * El precio **no se calcula aca**. Llega en `precioCentesimos` de la respuesta y se
 * formatea. Duplicar el motor de precios en el navegador es la forma mas rapida de que
 * la pantalla y el cobro dejen de coincidir (ADR-011).
 */

import { DURACIONES_MINUTOS, type DuracionMinutos } from '@padel/domain';

import type { OpcionDisponibilidad } from '@/infra/contratos';
import * as formato from '@/publico/dominio/formato';

interface PropiedadesSelectorDeDuracion {
  readonly localHora: string;
  readonly opciones: readonly OpcionDisponibilidad[];
  readonly duracionElegida: DuracionMinutos | null;
  readonly alElegir: (duracion: DuracionMinutos) => void;
}

/** Duraciones que la tabla de tarifas no ofrece en este inicio. */
function noOfrecidas(opciones: readonly OpcionDisponibilidad[]): readonly DuracionMinutos[] {
  return DURACIONES_MINUTOS.filter(
    (duracion) => !opciones.some((opcion) => opcion.duracionMinutos === duracion),
  );
}

export function SelectorDeDuracion({
  localHora,
  opciones,
  duracionElegida,
  alElegir,
}: PropiedadesSelectorDeDuracion): React.JSX.Element {
  const faltantes = noOfrecidas(opciones);

  return (
    <fieldset className="fichas">
      <legend className="fichas__titulo">Duracion</legend>

      <div className="fichas__lista fichas__lista--duraciones">
        {opciones.map((opcion) => {
          const id = `duracion-${String(opcion.duracionMinutos)}`;
          const etiqueta = [
            `${String(opcion.duracionMinutos)} minutos`,
            formato.precio(opcion.precioCentesimos),
            opcion.disponible ? null : 'no disponible',
          ]
            .filter((parte) => parte !== null)
            .join(', ');

          return (
            <div className="ficha ficha--ancha" key={opcion.duracionMinutos}>
              {/* Nombre accesible completo y con puntuacion: ver GrillaDeCancha. */}
              <input
                aria-label={etiqueta}
                checked={duracionElegida === opcion.duracionMinutos}
                disabled={!opcion.disponible}
                id={id}
                name="duracion"
                onChange={() => {
                  alElegir(opcion.duracionMinutos);
                }}
                type="radio"
                value={opcion.duracionMinutos}
              />
              <label htmlFor={id}>
                {/* "60 minutos" y no "1 hora": son las dos unicas opciones y conviene
                    que se distingan de un vistazo, tambien al oido de un lector. */}
                <span className="ficha__principal">{`${String(opcion.duracionMinutos)} minutos`}</span>
                <span className="ficha__secundario">{formato.precio(opcion.precioCentesimos)}</span>
                {opcion.disponible ? null : <span className="ficha__estado">No disponible</span>}
              </label>
            </div>
          );
        })}
      </div>

      {faltantes.map((duracion) => (
        <p className="nota" key={duracion}>
          A las {localHora} no se ofrecen turnos de {String(duracion)} minutos: esa combinacion no
          esta en la tabla de tarifas del complejo, asi que no es reservable.
        </p>
      ))}
    </fieldset>
  );
}
